import * as React from 'react';
import { getDic } from '../../assets/i18n/dictionary';
import { store } from '../../store';
import * as actions from '../../store/actions';
import {
  Alert,
  Loading,
  Container,
} from '../../components';
import { useNavigate } from "react-router-dom";
import faceApiService from '../../services/faceApiService';
import urlUtils from '../../utils/urlUtils';
import fileUtils from '../../utils/fileUtils';

import CameraScan from './CameraScan';
import EmbedInstructions from './EmbedInstructions';

function getCurrentDimension() {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  }
}

const FaceScanPage = (props) => {

  const navigate = useNavigate();
  const params = urlUtils.getSearchParams();
  const [dimensions, setDimensions] = React.useState(getCurrentDimension());
  const [loadingControl, setLoadingControl] = React.useState({
    open: true,
    message: getDic("carregando")
  });
  const [alertControl, setAlertControl] = React.useState({
    open: false,
    title: '',
    message: '',
    icon: 'info',
  });
  const [faceScanControl, setFaceScanControl] = React.useState({
    eventId: '',
    system: '',
    openPreForm: false,
    openCamera: false,
    useBackCamera: false,
  });
  const [labeledFaceDescriptors, setLabeledFaceDescriptors] = React.useState();

  React.useEffect(() => {
    const updateDimension = () => {
      setDimensions(getCurrentDimension())
    }
    window.addEventListener('resize', updateDimension);
    return (() => {
      window.removeEventListener('resize', updateDimension);
    })
  }, [dimensions]);

  React.useEffect(() => {
    getPageData();
  }, []);

  const getPageData = React.useCallback(async () => {
    try {
      if ((!store.getState().user.id || !store.getState().user.email) && params?.public !== 'true') {
        navigate('/Login', { replace: true });
      }

      await faceApiService.loadModels();

      await getTestImageAndTestFaceDetection();

      await getPreForm();

      createEventListener();

      window.top.postMessage(JSON.stringify({
        message: 'modelsLoaded',
        data: null
      }), '*');

    } catch (error) {
      console.log('Error getPageData', error);
      setAlertControl({
        open: true,
        title: getDic("erro"),
        message: getDic("enviado-erro"),
        icon: 'error'
      });
    } finally {
      setLoadingControl({ ...loadingControl, open: false });
    }

    async function getTestImageAndTestFaceDetection() {
      let _res = await fetch(require('../../assets/test_face.jpg'));
      let _blob = await _res.blob();
      let _file = new File([_blob], "test_face.jpg", { type: _blob.type });
      let _base64 = await fileUtils.convertFileToBase64(_file);
      await faceApiService.getFaceDetectionFromBase64ImageCapture(_base64);
    }

    async function getPreForm() {
      try {
        let _faceScanControl = { ...faceScanControl };

        if (
          params?.public === 'true' && (
            !params?.eventId ||
            !params?.system
          )) {
          navigate('/Login', { replace: true });
        }

        if (params?.eventId) {
          _faceScanControl.eventId = params.eventId;
        }
        if (params?.system) {
          _faceScanControl.system = params.system;
        }
        if (params?.useBackCamera) {
          _faceScanControl.useBackCamera = params.useBackCamera === 'true' ? true : false;
        }

        if (!_faceScanControl.eventId || !_faceScanControl.system) {
          _faceScanControl.openPreForm = true;
        } else {
          _faceScanControl.openCamera = true;
        }

        setFaceScanControl(_faceScanControl);

      } catch (error) {
        console.log('Error getPreForm', error);
        throw error;
      }
    }
  }, []);

  const createEventListener = React.useCallback(async () => {
    let buffer = [];
    let expectedChunks = 0;

    async function handleMessage(event) {
      try {

        const _msg = JSON.parse(event.data);
        let _obj;

        if (_msg.type === "chunk") {
          buffer[_msg.index] = _msg.data;
          expectedChunks = _msg.total;

          if (buffer.filter(Boolean).length === expectedChunks) {
            const _fullMessage = buffer.join("");
            buffer = [];
            expectedChunks = 0;
            _obj = JSON.parse(_fullMessage);
          }
        } else {
          _obj = JSON.parse(_msg);
        }

        if (_obj && _obj.message === 'faceDetections') {
          let _labeledFaceDescriptors = await loadDetections(_obj.data);
          setLabeledFaceDescriptors(_labeledFaceDescriptors);
          window.top.postMessage(JSON.stringify({
            message: 'faceDetectionsLoaded',
            data: null
          }), '*');
          setFaceScanControl({
            ...faceScanControl,
            openPreForm: false,
            openCamera: true,
          });;
        }

        // if (_obj && _obj.message === 'faceDetectionsBack') {
        //   labeledFaceDescriptors = await loadDetections(_obj.data);
        //   await startVideo("environment");
        //   window.ReactNativeWebView.postMessage(JSON.stringify({
        //     message: 'faceDetectionsLoaded',
        //     data: null
        //   }));
        // }

      } catch (err) {
        console.error("Error handleMessage", err);
      }
    }

    document.addEventListener("message", handleMessage);
    window.addEventListener("message", handleMessage);

    async function loadDetections(detections = []) {
      return Promise.all(
        detections.map(async (detection) => {
          let _descriptions = parseJsonString(detection.detection);
          let _labeledFaceDescriptors = await faceApiService.loadDetection(detection, _descriptions);
          return _labeledFaceDescriptors;
        })
      );

      function parseJsonString(jsonStr = '') {
        let _decodedJson = JSON.parse(jsonStr, function (key, value) {
          try {
            if ("flag" in value && value.flag === 'FLAG_TYPED_ARRAY') {
              switch (value.constructor) {
                case 'Float32Array':
                  return new Float32Array(value.data);
                case 'Int32Array':
                  return new Int32Array(value.data);
                case 'Uint8Array':
                  return new Uint8Array(value.data);
                default:
                  return value.data;
              }
            }
          } catch (e) { }
          return value;
        });
        return _decodedJson;
      }
    }
  }, [faceScanControl, loadingControl]);

  const tryDetectFace = React.useCallback((detection) => {
    try {
      window.top.postMessage(JSON.stringify({
        message: 'faceDetected',
        data: detection._label,
      }), '*');

    } catch (error) {
      console.log('Error tryDetectFace', error);
    }
  }, [faceScanControl]);

  return (
    <Container
      header={params?.public === 'true' ? null : `${getDic("reconhecimento-facial")}`}
      padding={params?.public === 'true' ? 0 : 10}
      backButton={params?.public === 'true' ? false : true}
    >
      {params?.public !== 'true' && !faceScanControl.openCamera && (
        <EmbedInstructions
          dimensions={dimensions}
        />
      )}
      {faceScanControl.openCamera && labeledFaceDescriptors && (
        <CameraScan
          params={params}
          dimensions={dimensions}
          tryDetectFace={tryDetectFace}
          labeledFaceDescriptors={labeledFaceDescriptors}
          useBackCamera={faceScanControl.useBackCamera}
        />
      )}

      {modalControl()}
    </Container>
  );

  function modalControl() {
    if (alertControl.open) {
      return (
        <Alert
          open={alertControl.open}
          title={alertControl.title}
          message={alertControl.message}
          icon={alertControl.icon}
          onPress={() => setAlertControl({ ...alertControl, open: false, icon: 'info' })}
        />
      )
    }
    if (loadingControl.open) {
      return <Loading open={loadingControl.open} loadingMessage={loadingControl.message} />
    }

    return null;
  }
};

export default FaceScanPage;
